/**
 * Demo Battery Passport — inline snapshot used by the /passport/demo
 * marketing route. Does NOT hit the backend; renders instantly so
 * prospective users can see exactly what a shared EVIQ Battery Passport
 * looks like before they've connected their car.
 *
 * The passport carries an "EVIQ - EXAMPLE" watermark badge so it can't
 * be mistaken for a real, verified passport.
 */

const now = new Date();
const created = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);  // issued 2 days ago
const expires = new Date(created.getTime() + 90 * 24 * 60 * 60 * 1000);  // 90 day TTL

export const DEMO_PASSPORT = {
  passport_id: "EVIQ-DEMO-001",
  created_at: created.toISOString(),
  expires_at: expires.toISOString(),
  is_demo: true,
  snapshot: {
    // Vehicle identity
    vin: "5YJ3E1EA7KF000123",
    year: 2021,
    model: "Model 3",
    trim: "Long Range AWD",
    // Core numbers
    soh_pct: 94.0,
    delta_30d_pct: -0.18,
    epa_when_new_mi: 353,
    pack_kwh: 82,
    warranty_years: 8,
    warranty_miles: 120000,
    warranty_threshold_pct: 70,
    // Derived pillar metrics
    risk_score: 12,
    risk_band: "Low",
    thermal_stress_index: 22,
    charging_quality_score: 87,
    calibration_confidence_pct: 94,
    fleet_rank_pct: 12,           // top 12% (aging slower than 88% of peers)
    battery_value_usd: 11_800,
    forecast_3yr_soh: 88.6,
    forecast_8yr_soh: 78.9,
    eviq_score: 87,
    eviq_score_grade: "Excellent",
    // EVIQ Battery Passport™ v2
    trust_score: 97,
    trust_score_breakdown: {
      soh: 33, velocity: 14, charging: 9, thermal: 9,
      software: 5, fleet_rank: 5, warranty: 4, not_rapid: 2,
      repaired_penalty: 0, replaced_bonus: 0,
    },
    legendary_badge: {
      threshold: 95,
      days_required: 90,
      days_qualified: 217,
      first_qualified_at: "2025-07-01T00:00:00Z",
      awarded_at: created.toISOString(),
    },
    health_grade: "A+",
    remaining_capacity_pct: 93.8,
    estimated_remaining_life_years: 12.6,
    fast_charge_pct: 19,
    ac_charge_pct: 81,
    battery_cycles: 482,
    avg_soc_pct: 62,
    avg_daily_miles: 31,
    heat_exposure: "Low",
    cold_exposure: "Very Low",
    rapid_degradation_detected: false,
    battery_repaired: false,
    battery_replaced: false,
    current_software_verified: true,
    current_software_version: "2026.4.5",
    predicted_range_loss_3yr_pct: 4.2,
    legacy_timeline: [
      { ts: new Date(now.getTime() - 5 * 86400e3).toISOString(), kind: "trend", title: "Degradation velocity 0.18% / month", detail: "Well inside industry median", tone: "green" },
      { ts: new Date(now.getTime() - 12 * 86400e3).toISOString(), kind: "charging", title: "Charging pattern", detail: "81% AC · 19% Fast · 274 sessions logged", tone: "green" },
      { ts: new Date(now.getTime() - 28 * 86400e3).toISOString(), kind: "firmware", title: "Tesla firmware 2026.4.5", detail: "Installed at 41,820 mi", tone: "green" },
      { ts: "2024-08-14T00:00:00Z", kind: "service", title: "tire rotation", detail: "24,318 mi · Discount Tire" , tone: "amber" },
      { ts: new Date(now.getTime()).toISOString(), kind: "climate", title: "Climate exposure · temperate", detail: "Heat Low · Cold Very Low", tone: "cyan" },
      { ts: "2021-01-01T00:00:00Z", kind: "milestone", title: "Pack manufactured (2021 Model 3)", detail: "Original EPA range 353 mi · 82 kWh pack", tone: "cyan" },
    ],
    generated_by: "EVIQ Battery Intelligence · Example",
  },
};
